/** Raised when "clear local data" could not remove everything it was asked to. */
export class LocalPurgeError extends Error {
  constructor(readonly failures: string[]) {
    super(`Some local data could not be removed: ${failures.join('; ')}`);
    this.name = 'LocalPurgeError';
  }
}

/**
 * Deletes an IndexedDB database. Rejects when deletion fails, or when another tab
 * keeps the database open and nothing was deleted yet.
 */
export function deleteIndexedDatabase(name: string): Promise<void> {
  if (typeof indexedDB === 'undefined') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error(`${name} could not be deleted`));
    request.onblocked = () => reject(new Error(`${name} is open in another tab`));
  });
}

// Databases owned by packages that @yada/sync cannot import. Their modules close
// cached connections on `versionchange`, so deleting them here is not blocked.
const APP_OWNED_DATABASES = ['yada_chat_attachments'];
const APP_DATABASE_PREFIX = 'yada-app-';

/** Deletes app-owned local databases: chat attachments and every app's `yada-app-*` store. */
export async function clearAppDatabases(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  if (typeof indexedDB.databases !== 'function') {
    throw new Error('app databases could not be listed in this browser');
  }
  const appDatabases = (await indexedDB.databases())
    .map((info) => info.name)
    .filter((name): name is string => !!name && name.startsWith(APP_DATABASE_PREFIX));
  const results = await Promise.allSettled(
    [...APP_OWNED_DATABASES, ...appDatabases].map((name) => deleteIndexedDatabase(name)),
  );
  const failures = results.flatMap((r) => (r.status === 'rejected' ? [String((r.reason as Error)?.message ?? r.reason)] : []));
  if (failures.length) throw new Error(failures.join('; '));
}
